import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import {
  validateDiscountReasonField,
  validationMessages,
  MIN_DISCOUNT_REASON_FIELD_LENGTH,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const DiscountReasons = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [discountReasonInput, setDiscountReasonInput] = useState<string | null>(
    null,
  );
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [discountReasonList, setDiscountReasonList] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [discountReasonError, setDiscountReasonError] =
    useState<boolean>(false);
  const [discountReasonErrorMessage, setDiscountReasonErrorMessage] =
    useState<string>("");
  useEffect(() => {
    if (fieldValues) {
      setDiscountReasonList(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(discountReasonList);
    editClicked &&
      setSelectedId(
        checkData(discountReasonList, selectedId) ? selectedId : null,
      );
  }, [discountReasonList]);

  const payload = {
    "Discount Reason": discountReasonInput,
    Active: active,
  };

  const validateDiscountReasonInput = (): boolean => {
    if (!discountReasonInput || discountReasonInput.trim().length === 0) {
      setAlertMessage("Discount reason is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    setDiscountReasonError(false);
    setDiscountReasonErrorMessage("");

    if (discountReasonInput.trim().length < MIN_DISCOUNT_REASON_FIELD_LENGTH) {
      setDiscountReasonError(true);
      setDiscountReasonErrorMessage(validationMessages.discountReasonMinLength);
      return false;
    }

    if (!validateDiscountReasonField(discountReasonInput)) {
      setDiscountReasonError(true);
      setDiscountReasonErrorMessage(validationMessages.discountReason);
      return false;
    }

    return true;
  };

  const updateDiscountReasons = () => {
    if (!validateDiscountReasonInput()) {
      return;
    }
    if (
      checkDuplicate(
        discountReasonList.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.DISCOUNTREASON,
        discountReasonInput,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setDiscountReasonList((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editDiscountReasons(null);
    setAlertMessage(`${GolfPropertyEnum.discountReasons} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addDiscountReasons = () => {
    if (!validateDiscountReasonInput()) {
      return;
    }
    if (
      checkDuplicate(
        discountReasonList,
        DNDFieldsEnum.DISCOUNTREASON,
        discountReasonInput,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setDiscountReasonList((prev: any[]) =>
      updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord({ ...payload, Active: active });
    editDiscountReasons(null);
  };

  const editDiscountReasons = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setDiscountReasonInput(list?.[DNDFieldsEnum.DISCOUNTREASON] ?? null);
    setActive(list?.Active ?? true);
    setDiscountReasonError(false);
    setDiscountReasonErrorMessage("");
  };

  const deleteDiscountReasons = (id: number) => {
    setDiscountReasonList(deleteItem(discountReasonList, id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Discount Reasons
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.discountReason)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editDiscountReasons(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editDiscountReasons(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Discount Reason
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.discountReason)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="DiscountReasons"
                variant="outlined"
                placeholder="Discount Reason"
                value={discountReasonInput ?? ""}
                error={discountReasonError}
                helperText={
                  discountReasonError ? discountReasonErrorMessage : ""
                }
                sx={{ width: "100%" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDiscountReasonInput(e.target.value);
                  setDiscountReasonError(false);
                  setDiscountReasonErrorMessage("");
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateDiscountReasons() : addDiscountReasons()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Discount Reason
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Discount Reasons Available</Typography>
            <Stack direction="column" gap="10px">
              {discountReasonList ? (
                <DraggableItemList
                  itemData={discountReasonList}
                  onOrderUpdate={(order) => setDiscountReasonList(order)}
                  onEdit={(info) => editDiscountReasons(info)}
                  onDelete={(id) => deleteDiscountReasons(id)}
                  code={DNDFieldsEnum.DISCOUNTREASON}
                  name={DNDFieldsEnum.ACTIVE}
                />
              ) : (
                <Typography variant="body2">
                  No Discount Reasons Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default DiscountReasons;
